import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import styled from "styled-components";
import TuneIcon from "@mui/icons-material/Tune";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import "./DataInvestigationReportsDialog.scss";
import CloseIcon from "@mui/icons-material/Close";
import Label from "../Label/Label";
import { useDispatch, useSelector } from "react-redux";
import * as acts from "../../redux/investigation/actions";
import DashboardTableSkaleton from "../Skeletons/DashboardTableSkaleton";
import CustomizedSnackbars from "../Snackbars/CustomizedSnackbars";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 800,
    width: 650,
    padding: "2px !important",
    margin: "0px !important",
  },
});

function Row(props) {
  const { list } = props;
  const dispatch = useDispatch();

  const onClickOpenPreview = (url) => {
    dispatch(acts.previewBatchRecords(url));
  };

  return (
    <React.Fragment>
      <Grid container className="invistegator-report-dialog-table-data">
        <Grid item xs={8} className="report-dialog-overflow-hidden">
          {list?.report_name}
        </Grid>
        <Grid item xs={2} className="report-dialog-overflow-hidden">
          {list?.created_on}
        </Grid>
        <Grid item xs={1} className="report-dialog-overflow-hidden">
          <IconButton
            aria-label="delete"
            onClick={() => onClickOpenPreview(list?.report_path)}
            color="inherit"
            variant="outlined"
            className="blue-icon"
          >
            <RemoveRedEyeIcon />
          </IconButton>
        </Grid>
        <Grid item xs={0.5}>
          <CustomWidthTooltip
            arrow
            placement="left"
            title={
              <TableContainer component={Paper}>
                <Table size="small" aria-label="report table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ padding: "5px" }}>
                        Parameter Name
                      </TableCell>
                      <TableCell align="left" sx={{ padding: "5px" }}>
                        Range
                      </TableCell>
                      <TableCell align="left" sx={{ padding: "5px" }}>
                        Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list &&
                      list?.report_json &&
                      list?.report_json?.length > 0 &&
                      list?.report_json?.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell align="left">
                            {row?.parameterName}
                          </TableCell>
                          <TableCell align="left">{row?.rangeType}</TableCell>
                          <TableCell align="left">
                            {row?.rangeType !== "Exact"
                              ? `${row?.minValue} - ${row?.maxValue}`
                              : row?.minValue}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          >
            <TuneIcon
              aria-label="delete"
              color="inherit"
              variant="outlined"
              className="blue-icon"
            />
          </CustomWidthTooltip>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

const DataInvestigationReportsDialog = ({ openReportsDialog, handleClose }) => {
  const dispatch = useDispatch();

  const repostListAPiStatus = useSelector(
    (state) => state?.investigation?.repostListAPiStatus
  );
  const reportsDataList = useSelector(
    (state) => state?.investigation?.reportsDataList
  );

  const [message, setMessage] = React.useState("");
  const [err, setErr] = React.useState(false);
  const [loader, setLoader] = React.useState(true);

  const descriptionElementRef = React.useRef(null);

  const reportDataUrl = useSelector(
    (state) => state?.investigation?.previewBatchRecords
  );

  React.useEffect(() => {
    if (reportDataUrl) {
      window.open(reportDataUrl, "_blank");
      dispatch(acts.clearPreviewBatchRecords());
    }
  }, [reportDataUrl]);

  React.useEffect(() => {
    dispatch(acts.clearAllPreviousReportsApiStatus());
    dispatch(acts.getFilterationReporstList());
  }, []);

  React.useEffect(() => {
    if (repostListAPiStatus && repostListAPiStatus !== undefined) {
      if (repostListAPiStatus?.apiError === true) {
        setLoader(false);
        setMessage(repostListAPiStatus?.message);
        setErr(true);
        dispatch(acts.clearAllPreviousReportsApiStatus());
      } else if (repostListAPiStatus?.apiSuccess === true) {
        setLoader(false);
        dispatch(acts.clearAllPreviousReportsApiStatus());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repostListAPiStatus]);

  React.useEffect(() => {
    if (openReportsDialog) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [openReportsDialog]);

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        open={openReportsDialog}
        onClose={() => handleClose()}
        PaperProps={{ style: { minHeight: "calc(100% - 10px)" } }}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="scroll-dialog-title" sx={{ padding: "5px  20px " }}>
          Data Investigation Reports
          <IconButton
            aria-label="close"
            onClick={handleClose}
            className="close-icon"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers={"paper"}>
          <Grid container>
            <Grid item md={12} sm={6} xs={2}>
              <Box className="investigator-report-dialog-table-wrap">
                <Grid container className="tableHeader">
                  <Grid item xs={8}>
                    Report Name
                  </Grid>
                  <Grid item xs={2}>
                    Created On
                  </Grid>
                  <Grid item xs={1}>
                    Preview
                  </Grid>
                  <Grid item xs={1}>
                    Filters
                  </Grid>
                </Grid>

                {repostListAPiStatus?.loader ? (
                  <DashboardTableSkaleton />
                ) : reportsDataList && reportsDataList?.length > 0 ? (
                  <Grid>
                    {reportsDataList?.map((list, i) => (
                      <Row key={i} list={list} i={i} />
                    ))}
                  </Grid>
                ) : (
                  <div className="no-data">
                    <Label
                      text="No Data Found"
                      fontSize="16"
                      fontWeight="600"
                      padding="20px"
                    />
                  </div>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {err && (
        <CustomizedSnackbars
          severity="error"
          state={err}
          text={message}
          handleClose={() => setErr(false)}
        />
      )}
    </React.Fragment>
  );
};

export default DataInvestigationReportsDialog;
